import { useMemo } from 'react';

import {
  useDirectoryGetProductionCalendar,
  useCreateOrder as useCreateOrderApi,
  useOrderGetNewSchema,
} from 'api/requests';
import { OrderOperationType } from 'api/types/entity/order';
import { convertDateByTimezone, lodash, setStartOfDay } from 'helpers';
import { useServerErrorNotify } from 'hooks';
import { useTranslation } from 'libs/i18n';
import { ROUTES, RoutesId, useNavigate } from 'libs/navigation';
import { OrderFormType } from 'modules/order/common/forms/orderForm';

import { getOrderInitialValues } from '../helpers/getOrderInitialValues';
import { getOrderSubmitData } from '../helpers/getOrderSubmitData';

export const useCreateOrder = ({
  orderType,
}: {
  orderType: OrderOperationType;
}) => {
  const { t } = useTranslation();

  const { navigate } = useNavigate();

  const { orderSchema, loading: loadingNewSchema } = useOrderGetNewSchema();

  const { productionCalendar, loading: loadingProductionCalendar } =
    useDirectoryGetProductionCalendar();

  const {
    createOrder,
    loading: submitLoading,
    error: submitError,
  } = useCreateOrderApi();

  const initialValues = useMemo(
    () => getOrderInitialValues(orderType, orderSchema),
    [orderType, orderSchema]
  );

  // GMT+5 (Алматы)
  // TODO: Feature request to get broker timezone offset from API
  const brokerTimezoneOffset = 18000;

  const minEndDate = useMemo(() => {
    if (!orderSchema?.minEndDate) return undefined;
    return setStartOfDay(
      convertDateByTimezone(
        new Date(orderSchema.minEndDate),
        brokerTimezoneOffset
      )
    );
  }, [orderSchema?.minEndDate]);

  const disabledDates = lodash
    .compact(productionCalendar?.nonBusinessDays)
    .map((date) => convertDateByTimezone(new Date(date), brokerTimezoneOffset));

  const title = t(`order.operationTypes.${orderType}`);

  const breadcrumbsTitles = {
    [RoutesId.createOrder]: title,
  };

  const onBack = () => {
    navigate(ROUTES.orders.fullPath, {});
  };

  const onSubmit = async (values: OrderFormType) => {
    const params = getOrderSubmitData(orderType, values);
    const orderId = await createOrder(params);
    if (orderId) {
      onBack();
    }
  };

  useServerErrorNotify(submitError);

  return {
    submitLoading,
    initialValues,
    loading: loadingNewSchema || loadingProductionCalendar,
    disabledDates,
    orderSchema,
    minEndDate,
    title,
    breadcrumbsTitles,
    brokerTimezoneOffset,
    onSubmit,
    onBack,
  };
};
